// src/pages/HowItWorks.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import GradientButton from '../components/GradientButton';

const HowItWorks = () => {
  const navigate = useNavigate();

  return (
    <div style={{ 
      fontFamily: "'Open Sans', sans-serif",
      backgroundColor: '#f8fafc',
      minHeight: '100vh'
    }}>
      {/* 1. Hero Section */}
<div style={{
  background: 'linear-gradient(135deg, #FA8609 0%, #E6761A 100%)',
  borderRadius: '16px',
  padding: '2rem 1rem',
  textAlign: 'center',
  margin: '1rem',
  boxShadow: '0 8px 32px rgba(250, 134, 9, 0.3)'
}}>
        <h1 style={{
          margin: '0 0 1rem 0',
          color: '#FFF',
          fontWeight: 'bold',
          fontSize: 'clamp(2rem, 8vw, 3rem)',
          textShadow: '2px 2px rgba(0,0,0,0.2)',
          lineHeight: '1.2'
        }}>
          How LuckyLogic.ai Works
        </h1>
        <p style={{
          color: '#FFF',
          fontSize: 'clamp(1rem, 4vw, 1.25rem)',
          margin: 0,
          opacity: 0.95,
          maxWidth: '600px',
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          AI intelligence that engineers smarter lottery numbers for you
        </p>
      </div>

      <div style={{
        padding: '2rem 1rem',
        maxWidth: '900px',
        margin: '0 auto'
      }}>
       {/* 2. Science Behind Your Numbers explanation */}
<div style={{
  backgroundColor: 'white',
  borderRadius: '16px',
  padding: '2rem',
  marginBottom: '2rem',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
}}>
  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem' }}>
    <span style={{ fontSize: '2rem', marginRight: '1rem' }}>🧠</span>
    <h2 style={{ 
      margin: 0, 
      fontSize: 'clamp(1.5rem, 5vw, 2rem)',
      color: '#1f2937',
      fontWeight: '700'
    }}>
      The Science Behind Your Numbers
    </h2>
  </div>
  
  <p style={{
    fontSize: 'clamp(1rem, 3vw, 1.125rem)',
    lineHeight: '1.6',
    color: '#4b5563',
    margin: '0 0 1.5rem 0'
  }}>
    At LuckyLogic, we don’t believe in picking numbers that are completely random and thoughtless. Instead, your numbers should be specific, strategically informed, and grounded in real intelligence and logic. Our advanced AI system pulls in the latest draw results from both Powerball and Mega Millions, then processes years of historical data through sophisticated logic filters. By analyzing number frequencies, we can estimate when certain numbers may be more likely to reappear, while pattern recognition across ball combinations allows us to emphasize sets with stronger possibilities than uninformed selections.
  </p>
  
  <p style={{
    fontSize: 'clamp(1rem, 3vw, 1.125rem)',
    lineHeight: '1.6',
    color: '#4b5563',
    margin: '0 0 2rem 0'
  }}>
    Our number generation pipeline starts by combining the most recent winning results with years of historical data. That data is then run through a series of AI-driven logic filters that remove some numbers, weight others, and emphasize certain combination trends. The outcome is millions of refined number sets—each one unique and shaped by logic and analysis rather than blind chance.
  </p>

{/* CTA Button */}
<div style={{ textAlign: 'center', marginTop: '1.5rem', overflow: 'visible' }}>
  <GradientButton
    variant="blue"
    onClick={() => navigate('/signup')}
    style={{
      maxWidth: '90%',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease',
      // this button intentionally avoids overflow:hidden so the hover lift isn't clipped
      overflow: 'visible',
      willChange: 'transform'
    }}
  >
    Sign Up Now
  </GradientButton>
</div>

</div>

{/* How It Works Video */}
<div style={{
  width: '100%',
  marginBottom: '2rem',
  position: 'relative',
  borderRadius: '12px',
  overflow: 'hidden',
}}>
  <video
    id="ll-hiw-video"
    ref={el => {
      window.__llHiwVideo = el;
      if (el && !window.__llHiwVideoAutoPlayTriggered) {
        window.__llHiwVideoAutoPlayTriggered = true;
        setTimeout(() => {
          const cover = document.getElementById('ll-hiw-video-cover');
          if (cover) cover.style.display = 'none';
          el.muted = true;
          el.play().catch(() => {
            if (cover) cover.style.display = 'flex';
          });
        }, 2000);
      }
    }}
    style={{
      width: '100%',
      height: 'auto',
      display: 'block',
      backgroundColor: '#000',
    }}
    src="/videos/LottoLogic_Howitworkks.mp4"
    type="video/mp4"
    controls
    playsInline
    onEnded={() => {
      if (window.__llHiwVideo) {
        window.__llHiwVideo.pause();
        window.__llHiwVideo.currentTime = 0;
        window.__llHiwVideo.muted = false;
      }
      const cover = document.getElementById('ll-hiw-video-cover');
      if (cover) cover.style.display = 'flex';
    }}
  />
  <div
    id="ll-hiw-video-cover"
    role="button"
    tabIndex={0}
    aria-label="Play video: Learn how LuckyLogic.ai works"
    onClick={() => {
      const v = window.__llHiwVideo;
      if (v) {
        v.muted = false;
        v.play();
        const cover = document.getElementById('ll-hiw-video-cover');
        if (cover) cover.style.display = 'none';
      }
    }}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const v = window.__llHiwVideo;
        if (v) {
          v.muted = false;
          v.play();
          const cover = document.getElementById('ll-hiw-video-cover');
          if (cover) cover.style.display = 'none';
        }
      }
    }}
    style={{
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem',
      cursor: 'pointer',
      backgroundImage: 'linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url("/images/ll-howitworks-video-cover.jpg")',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }}
  >
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '0.75rem',
      color: 'white',
      textAlign: 'center',
    }}>
      <div
        aria-hidden="true"
        style={{
          width: '56px',
          height: '56px',
          borderRadius: '9999px',
          background: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
          fontSize: '1.5rem',
          color: '#10b981',
        }}
      >
        ▶
      </div>
      <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>
        Click to see how LuckyLogic.ai Works
      </div>
    </div>
  </div>
</div>

{/* 3. Why Choose LuckyLogic.ai benefits (responsive, mobile-first) */}
<div style={{
  backgroundColor: 'white',
  borderRadius: '16px',
  padding: '2rem',
  marginBottom: '2rem',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
}}>
  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2rem' }}>
    <span style={{ fontSize: '2rem', marginRight: '1rem' }}>✨</span>
    <h2 style={{
      margin: 0,
      fontSize: 'clamp(1.5rem, 5vw, 2rem)',
      color: '#1f2937',
      fontWeight: '700'
    }}>
      Why Choose LuckyLogic.ai?
    </h2>
  </div>

  {/* Safety net: force single column on small screens even if anything else fights it */}
  <style>{`
    @media (max-width: 640px) {
      #why-grid {
        grid-template-columns: 1fr !important;
        gap: 1rem !important;
      }
    }
  `}</style>

  <div
    id="why-grid"
    style={{
      display: 'grid',
      // Same responsive pattern as HomePage: stacks on phones, 2+ cols when there's room
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '1rem'
    }}
  >
    {[
      {
        icon: '📊',
        title: 'Science Over Guessing',
        desc: 'We analyze years of lottery results to find number frequencies, trends, and combination patterns that random picks ignore.'
      },
      {
        icon: '🎯',
        title: 'Millions of Refined Options',
        desc: 'Our system filters numbers through algorithmic logic and generates millions of possible refined number sets.'
      },
      {
        icon: '🧩',
        title: 'Unique to You',
        desc: 'No two members receive the same sets. Your unique combinations are shaped by data-driven logic and exclusive to you.'
      },
      {
        icon: '⚡',
        title: 'Consistently Delivered',
        desc: 'Numbers arrive at least 24 hours before every drawing—sent to text and email so you’re ready to play.'
      }
    ].map((benefit, index) => (
      <div
        key={index}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          padding: '1rem',
          borderRadius: '12px',
          backgroundColor: '#f8fafc',
          // Prevent iOS from breaking words into single letters
          wordBreak: 'keep-all',
          WebkitHyphens: 'none',
          hyphens: 'none'
        }}
      >
        <div style={{ fontSize: '2rem', marginRight: '0.75rem', flexShrink: 0 }}>
          {benefit.icon}
        </div>
        <div>
          <h4
            style={{
              margin: '0 0 0.4rem 0',
              fontSize: '1.1rem',
              fontWeight: '700',
              color: '#1f2937',
              wordBreak: 'keep-all'
            }}
          >
            {benefit.title}
          </h4>
          <p
            style={{
              margin: 0,
              color: '#6b7280',
              fontSize: '0.95rem',
              lineHeight: '1.45',
              // Allow normal word wrapping without letter-by-letter breaks
              overflowWrap: 'break-word',
              whiteSpace: 'normal'
            }}
          >
            {benefit.desc}
          </p>
        </div>
      </div>
    ))}
  </div>
</div>


{/* 4. Choose Your Plan Section */}
<div style={{
  backgroundColor: 'white',
  borderRadius: '16px',
  padding: '2rem clamp(1rem, 4vw, 2rem)',
  marginBottom: '2rem',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  overflow: 'visible'
}}>
  {/* Header */}
  <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
      <span style={{ fontSize: '1.75rem' }}>💰</span>
      <h2 style={{ margin: 0, fontSize: 'clamp(1.5rem, 5vw, 2rem)', fontWeight: '700', color: '#1f2937' }}>
        Choose Your Plan
      </h2>
    </div>
    <p style={{ margin: 0, color: '#6b7280', fontSize: 'clamp(0.95rem, 3vw, 1.1rem)' }}>
      2 simple plans. One for everyday lottery players. One for players who buy tickets for massive jackpots.
    </p>
  </div>

  {/* Force 1 column on phones */}
  <style>{`
    @media (max-width: 640px) {
      #pricing-grid {
        grid-template-columns: 1fr !important;
        gap: 1rem !important;
      }
    }
  `}</style>

  {/* Plan Cards */}
  <div
    id="pricing-grid"
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '1.5rem',
      maxWidth: '900px',
      margin: '0 auto'
    }}
  >
    {/* Jackpot Chaser - MOST POPULAR */}
    <div style={{
      border: '2px solid #A6CE3A',
      borderRadius: '16px',
      padding: '1.5rem',
      backgroundColor: 'white',
      position: 'relative',
      boxShadow: '0 8px 24px rgba(166, 206, 58, 0.18)',
    }}>
      <div style={{
        position: 'absolute',
        top: '8px',
        right: '8px',
        backgroundColor: '#A6CE3A',
        color: 'white',
        padding: '0.25rem 0.5rem',
        borderRadius: '999px',
        fontSize: '0.65rem',
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}>
        MOST POPULAR
      </div>
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
          <span style={{ fontSize: 'clamp(2rem, 7vw, 2.5rem)', fontWeight: '800', color: '#1f2937' }}>$14.99</span>
          <span style={{ fontSize: '1rem', color: '#6b7280' }}>per year</span>
        </div>
        <p style={{ margin: '0.5rem 0 0 0', fontWeight: '700', color: '#1f2937', fontSize: '1.1rem' }}>
          🎯 Jackpot Chaser
        </p>
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {[
          'Numbers when jackpots hit your threshold',
          'Choose $500M+, $750M+, or $1B+',
          'Powerball & Mega Millions',
          'Text & email delivery',
          'Annual subscription (no cancellations)'
        ].map((item, i) => (
          <li key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.6rem', color: '#4b5563', fontSize: '0.95rem' }}>
            <span style={{ color: '#10b981', marginRight: '0.5rem', fontSize: '1.1rem' }}>✓</span>{item}
          </li>
        ))}
      </ul>
<p style={{ marginTop: '1rem', fontWeight: '700', color: '#16a34a', fontSize: '0.95rem', textAlign: 'center' }}>
        👉 Perfect for players who only play 500M+ Jackpots
      </p>
    </div>

        {/* Weekly Player - BEST VALUE */}
<div style={{
      border: '2px solid #1E9FF2',
      borderRadius: '16px',
      padding: '1.5rem',
      backgroundColor: '#f9fafb',
      position: 'relative',
    }}>
      <div style={{
        position: 'absolute',
        top: '8px',
        right: '8px',
        backgroundColor: '#1E9FF2',
        color: 'white',
        padding: '0.25rem 0.5rem',
        borderRadius: '999px',
        fontSize: '0.65rem',
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}>
        BEST VALUE
      </div>
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
          <span style={{ fontSize: 'clamp(2rem, 7vw, 2.5rem)', fontWeight: '800', color: '#1f2937' }}>$34.99</span>
          <span style={{ fontSize: '1rem', color: '#6b7280' }}>per year</span>
        </div>
        <p style={{ margin: '0.5rem 0 0 0', fontWeight: '700', color: '#1f2937', fontSize: '1.1rem' }}>
          🎲 Weekly Player
        </p>
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {[
          'Numbers for every drawing',
          'Or set your own threshold (All, 100M+, etc.)',
          'Powerball & Mega Millions',
          'Text & email delivery',
          'Annual subscription (no cancellations)'
        ].map((item, i) => (
          <li key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.6rem', color: '#4b5563', fontSize: '0.95rem' }}>
            <span style={{ color: '#10b981', marginRight: '0.5rem', fontSize: '1.1rem' }}>✓</span>{item}
          </li>
        ))}
      </ul>
<p style={{ marginTop: '1rem', fontWeight: '700', color: '#16a34a', fontSize: '0.95rem', textAlign: 'center' }}>
        👉 Perfect for players who play every week
      </p>
    </div>
  </div>
</div>

        {/* 5. Call to Action with Sign Up button */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '2rem',
          textAlign: 'center',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
        }}>
          <h2 style={{
            margin: '0 0 1rem 0',
            fontSize: 'clamp(1.5rem, 5vw, 2rem)',
            fontWeight: '700',
            color: '#1f2937'
          }}>
            Ready to Get Your AI-Generated Numbers?
          </h2>
          <p style={{
            margin: '0 0 2rem 0',
            color: '#6b7280',
            fontSize: '1.1rem',
            maxWidth: '500px',
            marginLeft: 'auto',
            marginRight: 'auto'
          }}>
            Join thousands of players using LuckyLogic.ai to get smarter lottery numbers.
          </p>
          
          <GradientButton
            variant="blue"
            onClick={() => navigate('/signup')}
            style={{ maxWidth: '90%' }}
          >
            Sign Up Now
          </GradientButton>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;